import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const ALL_ROLES = [
  { id: 'all', key: 'allRoles', color: 'dark' },
  { id: 'admin', key: 'role_admin', color: 'danger' },
  { id: 'dept_head', key: 'role_dept_head', color: 'primary' },
  { id: 'employee', key: 'role_employee', color: 'info' },
  { id: 'agent', key: 'role_agent', color: 'warning' },
  { id: 'manager', key: 'role_manager', color: 'indigo' },
  { id: 'representative', key: 'role_representative', color: 'success' },
  { id: 'customer', key: 'role_customer', color: 'secondary' }
];

const AdminUsers = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create Form State
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('dept_head');
  const [departmentId, setDepartmentId] = useState('');
  const [areaId, setAreaId] = useState('');

  // Edit Modal State
  const [editingUser, setEditingUser] = useState(null);
  const [editFullName, setEditFullName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editRole, setEditRole] = useState('dept_head');
  const [editDepartmentId, setEditDepartmentId] = useState('');
  const [editAreaId, setEditAreaId] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editMsg, setEditMsg] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, deptsRes, areasRes] = await Promise.all([
        api.get('/auth/users'),
        api.get('/departments'),
        api.get('/areas')
      ]);
      setUsers(usersRes.data);
      setDepartments(deptsRes.data);
      setAreas(areasRes.data);
      if (deptsRes.data.length > 0) setDepartmentId(deptsRes.data[0].id);
      if (areasRes.data.length > 0) setAreaId(areasRes.data[0].id);
    } catch (err) {
      console.error('Error fetching admin users data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg('');

    try {
      await api.post('/auth/users', {
        full_name: fullName,
        email,
        password,
        role,
        department_id: ['dept_head', 'employee', 'agent', 'manager'].includes(role) ? departmentId : null,
        area_id: role === 'representative' ? areaId : null
      });

      setFullName('');
      setEmail('');
      setPassword('');
      setMsg(`${t('createUserTitle')} - Success!`);
      fetchData();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to create user account.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (u) => {
    setEditingUser(u);
    setEditFullName(u.full_name || '');
    setEditEmail(u.email || '');
    setEditPassword('');
    setEditRole(u.role || 'dept_head');
    setEditDepartmentId(u.department_id || (departments.length > 0 ? departments[0].id : ''));
    setEditAreaId(u.area_id || (areas.length > 0 ? areas[0].id : ''));
    setEditMsg('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setEditSubmitting(true);
    setEditMsg('');

    try {
      await api.put(`/auth/users/${editingUser.id}`, {
        full_name: editFullName,
        email: editEmail,
        password: editPassword,
        role: editRole,
        department_id: ['dept_head', 'employee', 'agent', 'manager'].includes(editRole) ? editDepartmentId : null,
        area_id: editRole === 'representative' ? editAreaId : null
      });

      setMsg(`User "${editFullName}" updated successfully!`);
      setEditingUser(null);
      fetchData();
    } catch (err) {
      setEditMsg(err.response?.data?.message || 'Failed to update user account.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`${t('deleteConfirm')} (${name})`)) return;
    try {
      await api.delete(`/auth/users/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete user account.');
    }
  };

  // Compute counts per role
  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  const filteredUsers = filterRole === 'all' 
    ? users 
    : users.filter(u => u.role === filterRole);

  const getRoleBadgeClass = (r) => {
    switch (r) {
      case 'dept_head': return 'bg-primary';
      case 'employee': return 'bg-info text-white';
      case 'admin': return 'bg-danger';
      case 'representative': return 'bg-success';
      case 'agent': return 'bg-warning text-dark';
      case 'manager': return 'bg-purple text-white';
      default: return 'bg-secondary';
    }
  };

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="brand-font fw-bold mb-1">{t('staffUsers')} & {t('rolesMaster')}</h2>
          <p className="text-muted mb-0">Create, edit, and manage all officials, staff roles, ward representatives, and logins</p>
        </div>
        <div className="badge bg-primary fs-6 px-3 py-2 rounded-pill shadow-sm">
          <i className="bi bi-people-fill me-2"></i>
          {t('totalUsersCount')}: <strong>{users.length}</strong>
        </div>
      </div>

      {msg && <div className="alert alert-info py-2 small mb-4 alert-dismissible fade show">{msg}</div>}

      {/* 1. Master Roles Bar */}
      <div className="card glass-card border-0 p-3 mb-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="fw-bold mb-0 text-dark">
            <i className="bi bi-shield-check text-primary me-2"></i>
            {t('rolesMaster')}
          </h6>
          <span className="small text-muted">Click any role to filter</span>
        </div>
        <div className="d-flex flex-wrap gap-2 pt-1">
          {ALL_ROLES.map((r) => {
            const count = r.id === 'all' ? users.length : (roleCounts[r.id] || 0);
            const isSelected = filterRole === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setFilterRole(r.id)}
                className={`btn btn-sm d-flex align-items-center gap-2 rounded-pill px-3 py-1.5 transition-all ${
                  isSelected ? 'btn-primary shadow-sm' : 'btn-outline-secondary bg-white'
                }`}
              >
                <span>{t(r.key)}</span>
                <span className={`badge ${isSelected ? 'bg-white text-primary' : 'bg-secondary text-white'} rounded-pill`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="row g-4">
        {/* Create User Form */}
        <div className="col-lg-4">
          <div className="card glass-card border-0 p-4 sticky-top" style={{ top: '90px' }}>
            <h5 className="brand-font fw-bold mb-3 d-flex align-items-center">
              <i className="bi bi-person-plus-fill text-primary me-2"></i>
              {t('createUserTitle')}
            </h5>
            <form onSubmit={handleCreateUser}>
              <div className="mb-3">
                <label className="form-label fw-semibold small">{t('fullNameLabel')} *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Ramesh Patil"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small">{t('emailLabel')} *</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="name@cms.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small">{t('passwordLabel')} *</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small">{t('role')} *</label>
                <select
                  className="form-select"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  required
                >
                  <option value="dept_head">{t('role_dept_head')}</option>
                  <option value="employee">{t('role_employee')}</option>
                  <option value="agent">{t('role_agent')}</option>
                  <option value="manager">{t('role_manager')}</option>
                  <option value="representative">{t('role_representative')}</option>
                  <option value="admin">{t('role_admin')}</option>
                  <option value="customer">{t('role_customer')}</option>
                </select>
              </div>

              {['dept_head', 'employee', 'agent', 'manager'].includes(role) && (
                <div className="mb-3">
                  <label className="form-label fw-semibold small">{t('targetDepartment')} *</label>
                  <select
                    className="form-select"
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    required
                  >
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
                    ))}
                  </select>
                </div>
              )}

              {role === 'representative' && (
                <div className="mb-3">
                  <label className="form-label fw-semibold small">{t('selectAreaWard')} *</label>
                  <select
                    className="form-select"
                    value={areaId}
                    onChange={(e) => setAreaId(e.target.value)}
                    required
                  >
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
                    ))}
                  </select>
                </div>
              )}

              <button type="submit" className="btn btn-primary-gradient w-100 py-2.5 mt-2" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {t('registering')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2-circle me-1"></i>
                    {t('createUserTitle')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Users Directory Table */}
        <div className="col-lg-8">
          <div className="card glass-card border-0 p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
              <h5 className="brand-font fw-bold mb-0">
                <i className="bi bi-person-lines-fill text-primary me-2"></i>
                {t('staffUsers')} ({filteredUsers.length})
              </h5>
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">{t('role')}:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: '180px' }}
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  {ALL_ROLES.map((r) => (
                    <option key={r.id} value={r.id}>{t(r.key)}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-person-x fs-1 d-block mb-2 text-muted"></i>
                <p className="mb-0">No users found for this filter.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t('tableCustomer')}</th>
                      <th>{t('role')}</th>
                      <th>{t('tableDept')} / {t('ward')}</th>
                      <th className="text-end">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <div className="fw-bold text-dark">{u.full_name}</div>
                          <div className="small text-muted">{u.email}</div>
                        </td>
                        <td>
                          <span className={`badge ${getRoleBadgeClass(u.role)} px-2.5 py-1.5`}>
                            {t(`role_${u.role}`, u.role.replace('_', ' ').toUpperCase())}
                          </span>
                        </td>
                        <td>
                          {u.department_name ? (
                            <span className="badge bg-light text-primary border">
                              <i className="bi bi-building me-1"></i>{u.department_name}
                            </span>
                          ) : u.area_name ? (
                            <span className="badge bg-light text-success border">
                              <i className="bi bi-geo-alt me-1"></i>{u.area_name}
                            </span>
                          ) : (
                            <span className="text-muted small italic">Global / None</span>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              onClick={() => handleOpenEdit(u)}
                              className="btn btn-outline-primary"
                              title={t('editUserTitle')}
                            >
                              <i className="bi bi-pencil-square me-1"></i>
                              {t('edit')}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(u.id, u.full_name)}
                              className="btn btn-outline-danger"
                              title="Delete Account"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EDIT USER MODAL */}
      {editingUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded-4">
              <div className="modal-header border-bottom bg-light">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-pencil-square text-primary me-2"></i>
                  {t('editUserTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingUser(null)}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleUpdateUser}>
                <div className="modal-body p-4">
                  {editMsg && <div className="alert alert-danger py-2 small mb-3">{editMsg}</div>}

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('fullNameLabel')} *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFullName}
                      onChange={(e) => setEditFullName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('emailLabel')} *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('newPasswordOptional')}</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder={t('leaveBlankPassword')}
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                    />
                    <div className="form-text extra-small">{t('leaveBlankPassword')}</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('role')} *</label>
                    <select
                      className="form-select"
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      required
                    >
                      <option value="dept_head">{t('role_dept_head')}</option>
                      <option value="employee">{t('role_employee')}</option>
                      <option value="agent">{t('role_agent')}</option>
                      <option value="manager">{t('role_manager')}</option>
                      <option value="representative">{t('role_representative')}</option>
                      <option value="admin">{t('role_admin')}</option>
                      <option value="customer">{t('role_customer')}</option>
                    </select>
                  </div>

                  {['dept_head', 'employee', 'agent', 'manager'].includes(editRole) && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('targetDepartment')} *</label>
                      <select
                        className="form-select"
                        value={editDepartmentId}
                        onChange={(e) => setEditDepartmentId(e.target.value)}
                        required
                      >
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {editRole === 'representative' && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('selectAreaWard')} *</label>
                      <select
                        className="form-select"
                        value={editAreaId}
                        onChange={(e) => setEditAreaId(e.target.value)}
                        required
                      >
                        {areas.map((a) => (
                          <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="modal-footer border-top bg-light">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setEditingUser(null)}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary-gradient px-4"
                    disabled={editSubmitting}
                  >
                    {editSubmitting ? t('updatingUser') : t('updateUserBtn')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
